import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  ACTIVITY_TYPE_LABELS,
  BADGE_UNIVERSO_COLORS,
  type ActivityType,
  type BadgeUniverso,
} from 'shared';

// Small presentational pieces shared by the activity screens.

export function UniversoChip({ universo }: { universo: BadgeUniverso }) {
  const { bg, text } = BADGE_UNIVERSO_COLORS[universo];
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ backgroundColor: bg, color: text }}
    >
      {universo}
    </span>
  );
}

export function TypeChip({ type }: { type: ActivityType }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
      {ACTIVITY_TYPE_LABELS[type]}
    </span>
  );
}

interface FormFieldProps {
  label: string;
  htmlFor: string;
  error?: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}

export function FormField({ label, htmlFor, error, required, hint, children }: FormFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className="text-xs font-medium text-muted-foreground">
        {label}
        {required && (
          <span className="text-destructive ml-0.5" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground/60">{hint}</p>}
      {error && (
        <p className="text-xs text-destructive flex items-center gap-1" role="alert">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}

interface ConfirmDialogProps {
  title: string;
  children: React.ReactNode;
  confirmLabel: string;
  isPending: boolean;
  /** `danger` (the default) for removing something, `primary` for the rest. */
  tone?: 'danger' | 'primary';
  onConfirm: () => void;
  onClose: () => void;
}

/** A yes/no question before something that cannot be quietly undone. */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  isPending,
  tone = 'danger',
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const danger = tone === 'danger';
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-start gap-4">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
              danger ? 'bg-destructive/10' : 'bg-primary/10'
            }`}
          >
            <AlertTriangle className={`w-5 h-5 ${danger ? 'text-destructive' : 'text-primary'}`} />
          </div>
          <div>
            <h3 id="confirm-dialog-title" className="text-base font-semibold text-foreground">
              {title}
            </h3>
            <div className="text-sm text-muted-foreground mt-1">{children}</div>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Voltar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 transition-colors ${
              danger
                ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                : 'bg-primary text-primary-foreground hover:bg-primary/90'
            }`}
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function LoadingBlock() {
  return (
    <div className="flex items-center justify-center py-24" role="status" aria-label="A carregar">
      <Loader2 className="w-8 h-8 text-primary animate-spin" />
    </div>
  );
}

export function ErrorBlock({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-3" role="alert">
      <AlertTriangle className="w-8 h-8 text-destructive" />
      <p className="text-sm font-medium text-destructive">{message}</p>
    </div>
  );
}
